import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';
import nunjucks from 'nunjucks';
import Excel from 'shared/Excel';
import {
    generateDoorPosition,
    generateReturnDoor,
    getDoorFormValues,
    generateApplianceDoor,
    generateDoorDrawer,
} from 'components/customer/Preview3DCommon/values/door';
import {
    getDefaultFormValues,
    getDefaultValues,
} from 'components/customer/Preview3DCommon/values/default';
import {
    generateDrawerPosition,
    getDrawerFormValues,
    generateInnerDrawer,
    generateDrawers,
    getRunnerDimensions,
    generateDrawerFace,
} from 'components/customer/Preview3DCommon/values/drawer';
import {
    getShelvesFormValues,
    generateHorizontalShelves,
    generateComplexHorizontalShelves,
    generateComplexVerticalShelves,
    generateUpperAndLowerShelves,
    generateUpperAndLowerLeftRightShelves,
    generateKickerLadderFrames,
    generateBulkheadLadderFrames,
} from 'components/customer/Preview3DCommon/values/shelves';
import {
    generateMultiPart,
    getMultipartFormValues,
} from 'components/customer/Preview3DCommon/values/multipart';
import {
    getRangehoodFormValues,
    generateRangehoodValues,
} from 'components/customer/Preview3DCommon/values/rangehood';
import {
    generateWallOvenValues,
    getWallOvenFormValues,
} from 'components/customer/Preview3DCommon/values/wallOven';
import {
    RoomPlannerFormValues,
    RoomPlannerProductWithTemplate,
} from 'components/customer/RoomPlanner/types';
import {generateDrillingPositions} from 'components/customer/Preview3DCommon/values/drilling';

/**
 * Class representing the data processing logic for the Room Planner.
 * This class handles the generation and replacement of template variables
 * and dimensions for Room Planner products.
 */
class RoomPlannerData {
    /**
     * Replaces variables in a JSON string with the provided template variables.
     * @private
     * @param {string} jsonString - The JSON string containing variables to replace.
     * @param {TemplateVariable} replacements - The template variables to replace in the JSON string.
     * @return {string} - The JSON string with variables replaced.
     */
    private replaceVariables(
        jsonString: string,
        replacements: TemplateVariable
    ): string {
        const renderedTemplate = nunjucks.renderString(
            jsonString,
            replacements
        );

        return renderedTemplate;
    }

    /**
     * Replaces dimensions in a JSON string based on Room Planner form values and template variables.
     * This method generates various values for doors, drawers, shelves, multipart components, rangehoods,
     * and wall ovens, and evaluates expressions using the provided variables.
     * @public
     * @param {string} json - The JSON string containing dimensions to replace.
     * @param {RoomPlannerFormValues} valuesParams - The form values from the Room Planner.
     * @param {TemplateVariable} variables - The template variables used for generating values.
     * @param {boolean} isRobeProduct - Flag indicating if the product is a robe product.
     * @return {string} - The JSON string with dimensions replaced.
     */
    public replaceDimensions(
        json: string,
        valuesParams: RoomPlannerFormValues,
        variables: TemplateVariable,
        isRobeProduct: boolean
    ) {
        const values = valuesParams as unknown as RoomPlannerFormValues & {
            template_3d: RoomPlannerProductWithTemplate['template_3d'];
            ladder_frames: RoomPlannerProductWithTemplate['ladder_frames'];
            width2: number;
            length_depth: number;
            width: number;
            width_depth: number;
        };

        const isOvenDrawer =
            values.template_3d[0]?.attributes.template == 'BaseOvenDrawers.xml';
        const isOvenCabinet =
            values.template_3d[0]?.attributes.template == 'BaseOven.xml';
        const hasMicrowave = [
            'BaseMicrowaveDrawers.xml',
            'UpperMicrowavePair.xml',
        ].includes(values.template_3d[0]?.attributes.template);
        if (!isOvenDrawer && !variables.isWallOvenProduct && !isOvenCabinet) {
            values.oven_opening_height = 0;
        }
        if (!hasMicrowave && !variables.isWallOvenProduct) {
            values.microwave_opening_height = 0;
        }

        if (variables.isWallOvenProduct) {
            values.rangehood_opening_height = 0;
        }

        const hasDoor = typeof values.cabinet_door_gap !== 'undefined';
        const carcaseThickness = values.cabinet_carc_thickness ?? 0;
        const exteriorThickness = values.cabinet_ext_thickness ?? 0;

        const doorFormValues = getDoorFormValues(values);
        const drawerFormValues = getDrawerFormValues({
            ...values,
        });
        const shelvesFormValues = getShelvesFormValues(
            values,
            variables?.shelfType
        );
        const multipartFormValues = getMultipartFormValues(values);
        const rangehoodFormValues = getRangehoodFormValues(values);
        const wallOvenFormValues = getWallOvenFormValues(values);

        const doorValues = {
            doorDefault: hasDoor
                ? generateDoorPosition(variables, values, doorFormValues)
                : {},
            doorWithReturn: variables.isReturnProduct
                ? generateReturnDoor(variables, values, doorFormValues)
                : {},
            applianceDoor:
                variables.isApplianceBifoldProduct ||
                Boolean(
                    variables?.fields.find(
                        (field) => field.name === 'isApplianceRollUp'
                    )?.expression
                )
                    ? generateApplianceDoor(variables, values, doorFormValues)
                    : {},
            doorDrawer:
                doorFormValues.drawers &&
                doorFormValues.drawers.length > 0 &&
                hasDoor
                    ? generateDoorDrawer(doorFormValues)
                    : {},
        };

        const drawerValues = {
            drawerDefault: generateDrawerPosition(
                values,
                variables,
                {
                    ...drawerFormValues,
                    microwaveOpeningHeight: isOvenDrawer
                        ? undefined
                        : drawerFormValues.microwaveOpeningHeight,
                },
                // Move the checking into the variables
                hasDoor &&
                    ['DoorDrawer.xml', 'DoorsDrawer.xml'].some((ending) =>
                        values.template_3d[0]?.attributes.template.endsWith(
                            ending
                        )
                    ),
                0.2
            ),
            innerDrawers:
                (Boolean(variables.innerDrawerCount) ||
                    !!values.drawer_amount) &&
                !Boolean(variables.innerDrawerOnly)
                    ? generateInnerDrawer(values, variables, drawerFormValues)
                    : {},

            drawerFace: Boolean(variables.isDrawerFace)
                ? generateDrawerFace(drawerFormValues)
                : {},
        };

        if (Boolean(variables.innerDrawerOnly) && values.drawers?.length) {
            const innerDrawer = values.drawers[0];
            const runnerSpecs =
                values?.drawer_runner_specs ?? innerDrawer.drawer_runner_specs;
            const {height} = getRunnerDimensions(runnerSpecs);
            drawerValues.innerDrawers = generateDrawers(
                [
                    {
                        ...innerDrawer,
                        drawer_runner_specs: runnerSpecs,
                        drawer_face_height: height,
                        drawer_runner_height: height,
                    },
                ],
                drawerFormValues,
                variables,
                values,
                true,
                height,
                values.cabinet_panel_width
            );
        }

        const shelvesValues = {
            simpleShelves:
                typeof values.shelves !== 'undefined'
                    ? generateHorizontalShelves(
                          values,
                          {
                              ...shelvesFormValues,
                              ...(!isRobeProduct ? {toeKickHeight: 0} : {}),
                          },
                          variables
                      )
                    : {},
            complexHorizontalShelves: Boolean(
                shelvesFormValues?.shelvesHorizontalCount
            )
                ? generateComplexHorizontalShelves(values, shelvesFormValues)
                : {},
            complexVerticalShelves: Boolean(
                shelvesFormValues?.shelvesVerticalCount
            )
                ? generateComplexVerticalShelves(values, shelvesFormValues)
                : {},
            pantryShelves:
                Boolean(values?.upper_shelves) &&
                variables?.shelfType === 'PANTRY'
                    ? generateUpperAndLowerShelves(values, shelvesFormValues)
                    : {},
            broomShelves:
                Boolean(values?.upper_shelves) &&
                values?.cabinet_partition_width
                    ? generateUpperAndLowerLeftRightShelves(
                          values,
                          shelvesFormValues,
                          variables
                      )
                    : {},
            kicker:
                Boolean(values.ladder_frames) ||
                Boolean(values.cabinet_ladder_frames)
                    ? generateKickerLadderFrames(values, shelvesFormValues)
                    : {},
            bulkhead:
                Boolean(values.ladder_frames) ||
                Boolean(values.cabinet_ladder_frames)
                    ? generateBulkheadLadderFrames(values, shelvesFormValues)
                    : {},
        };

        const multipartValues = {
            multiPart:
                Boolean(multipartFormValues.microwaveOpeningHeight) ||
                Boolean(values?.upper_shelves && values?.lower_shelves)
                    ? generateMultiPart(values, multipartFormValues)
                    : {},
        };

        const rangehoodValues = {
            rangehood:
                variables?.hasRangehoodVent ||
                typeof values.rangehood_opening_height !== 'undefined'
                    ? generateRangehoodValues(values, rangehoodFormValues)
                    : {},
        };

        const wallOvenValues = {
            wallOven: variables?.isWallOvenProduct
                ? generateWallOvenValues(values, wallOvenFormValues)
                : {},
        };

        const drillingValues = {
            drilling: variables.hasDrillings
                ? generateDrillingPositions(values)
                : {},
        };

        const formValues: Record<string, unknown> = {
            default: getDefaultValues(
                values,
                getDefaultFormValues(values),
                false
            ),
            ...doorValues,
            ...drawerValues,
            ...shelvesValues,
            ...multipartValues,
            ...rangehoodValues,
            ...wallOvenValues,
            ...values,
            ...drillingValues,
        };

        variables.fields.forEach((f) => {
            const evaluated = Excel.calculate(f.expression, {
                ...values,
                exteriorThickness: exteriorThickness,
                carcaseThickness: carcaseThickness,
            });
            formValues[f.name] = evaluated;
        });

        return this.replaceVariables(json, {...formValues} as TemplateVariable);
    }
}

export default new RoomPlannerData();
